import {
  QTopPolicyViolationsCard,
  QAppVersionsWithMostPolicyViolationsCard,
  QAppsCountCard,
  QAppVersionPolicyViolationsOverTimeCard,
} from '@kw/quokka-ui';

import { Box } from '@mui/material';
import { DashboardAnalyticsDateRangeDays, DashboardAnalyticsMetrics } from './dashboardAnalytics';
import { useDashboardNavigation } from './useDashboardNavigation';

type PolicyViolationsProps = {
  dateRangeDays: DashboardAnalyticsDateRangeDays;
  metrics: DashboardAnalyticsMetrics | null | undefined;
};

const platforms = ['android', 'ios'] as const;

const summaryCardCellSx = {
  gridColumn: 'span 2',
  '& .MuiPaper-root': { height: '100%' },
};

export const PolicyViolations = ({ dateRangeDays, metrics }: PolicyViolationsProps) => {
  const { getAppReportHref, getApplicationsHref } = useDashboardNavigation(dateRangeDays);
  const { versionCount, androidVersionCount, iosVersionCount, uniqueVersionCount, uniqueAndroidVersionCount, uniqueIosVersionCount } =
    metrics?.policyViolationsVersionCount ?? {};

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(6, 1fr)',
        gap: '24px',
        '& .MuiPaper-root': { width: '100%' },
      }}
    >

      {/* Row 1 — policy violations over time */}
      <Box key="policy-violations-over-time" sx={{ gridColumn: 'span 6' }}>
        <QAppVersionPolicyViolationsOverTimeCard data={metrics?.policyViolationsOverTime ?? []} />
      </Box>


      {/* Row 2 — count for total, android, and ios app versions with violations */}
      <Box sx={summaryCardCellSx}>
        <QAppsCountCard
          data-testid="total-versions-with-violations-card"
          variant="totalVersionsWithViolations"
          versionsCount={versionCount}
          uniqueAppsCount={uniqueVersionCount}
          href={getApplicationsHref({ platform: 'all', policyViolations: 'has_violations' })}
        />
      </Box>
      <Box key={'android'} sx={summaryCardCellSx}>
        <QAppsCountCard
          data-testid={`android-versions-with-violations-card`}
          variant={'androidVersionsWithViolations'}
          versionsCount={androidVersionCount}
          uniqueAppsCount={uniqueAndroidVersionCount}
          href={getApplicationsHref({ platform: 'android', policyViolations: 'has_violations' })}
        />
      </Box>
      <Box key={'ios'} sx={summaryCardCellSx}>
        <QAppsCountCard
          data-testid={`ios-versions-with-violations-card`}
          variant={'iosVersionsWithViolations'}
          versionsCount={iosVersionCount}
          uniqueAppsCount={uniqueIosVersionCount}
          href={getApplicationsHref({ platform: 'ios', policyViolations: 'has_violations' })}
        />
      </Box>

      {/* Row 3: Top 10 Policy Violations — Android then iOS */}
      {platforms.map(platform => (
        <Box key={`top-policy-${platform}`} sx={{ gridColumn: 'span 3' }}>
          <QTopPolicyViolationsCard
            platform={platform}
            data={(platform === 'android' ? metrics?.topPolicyViolationsAndroid : metrics?.topPolicyViolationsIos) ?? []}
          />
        </Box>
      ))}

      {/* Row 4: App Versions With Most Policy Violations — Android then iOS */}
      {platforms.map(platform => (
        <Box key={`app-versions-policy-${platform}`} sx={{ gridColumn: 'span 3' }}>
          <QAppVersionsWithMostPolicyViolationsCard
            platform={platform}
            data={
              (platform === 'android'
                ? metrics?.appVersionWithMostPolicyViolationsAndroid
                : metrics?.appVersionWithMostPolicyViolationsIos) ?? []
            }
            getViewReportHref={item => getAppReportHref(item.analysisId)}
          />
        </Box>
      ))}
    </Box>
  );
};
